/**
 * Player Prop Card
 *
 * Rich card displaying player prop picks with full details:
 * - Pick direction (OVER/UNDER) and line
 * - ML prediction and confidence
 * - Vegas edge
 * - Streak data and badges
 * - H2H record
 * - Context signals
 */

import React from 'react';

// Sport badge backgrounds
const SPORT_BG = {
  nba: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  nhl: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  nfl: 'bg-green-500/20 text-green-400 border-green-500/30',
  mlb: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// Stat type display labels
const STAT_LABELS = {
  points: 'PTS',
  rebounds: 'REB',
  total_rebounds: 'REB',
  assists: 'AST',
  three_pointers: '3PM',
  three_pointers_made: '3PM',
  three_pm: '3PM',
  steals: 'STL',
  blocks: 'BLK',
  turnovers: 'TO',
  pts_reb: 'PTS+REB',
  pts_ast: 'PTS+AST',
  reb_ast: 'REB+AST',
  pts_reb_ast: 'PRA',
  goals: 'G',
  shots: 'SOG',
  hits: 'HITS',
  blocked_shots: 'BLK',
};

// Get streak badge based on hit rate
function getStreakBadge(hitRate) {
  if (hitRate >= 1.0) return { label: 'BLAZING', className: 'bg-gradient-to-r from-orange-500 to-red-500 text-white' };
  if (hitRate >= 0.8) return { label: 'HOT', className: 'bg-green-500/20 text-green-400 border border-green-500/30' };
  if (hitRate >= 0.7) return { label: 'TRENDING', className: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' };
  return null;
}

// Get context signal styling
function getContextSignalStyle(signal) {
  if (!signal) return null;
  const s = signal.toLowerCase();
  if (s === 'favorable') return { className: 'text-green-400', icon: '\u25B2' };
  if (s === 'unfavorable') return { className: 'text-red-400', icon: '\u25BC' };
  return { className: 'text-gray-400', icon: '\u25CF' };
}

// Get risk level styling
function getRiskStyle(risk) {
  if (!risk) return null;
  const r = risk.toLowerCase();
  if (r === 'low') return { className: 'bg-green-500/20 text-green-400 border-green-500/30' };
  if (r === 'elevated') return { className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
  if (r === 'high') return { className: 'bg-red-500/20 text-red-400 border-red-500/30' };
  return { className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
}

// Confidence ring component
function ConfidenceRing({ value, size = 48 }) {
  // composite_score is already 0-100 scale, don't multiply
  const percentage = Math.round(value || 0);
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color based on confidence
  const color = percentage >= 70 ? '#22c55e' : percentage >= 50 ? '#eab308' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="3"
          fill="transparent"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="3"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-text-primary">{percentage}</span>
      </div>
    </div>
  );
}

export default function PlayerPropCard({ pick, featured = false }) {
  const {
    sport,
    player_name,
    team,
    opponent_name,
    home_team,
    away_team,
    is_home,
    pick_direction,
    line,
    stat_type,
    composite_score,
    vegas_edge,
    risk_level,
    streak_hit_rate,
    current_streak,
    streak_context_signal,
    vs_opponent_games,
    vs_opponent_hits,
    vs_opponent_rate,
    player_home_rate,
    player_away_rate,
    opponent_strength,
    reasoning,
    sportsbook_odds,
  } = pick;

  const sportBg = SPORT_BG[sport] || SPORT_BG.nba;
  const statLabel = STAT_LABELS[stat_type] || stat_type?.toUpperCase();
  const streakBadge = streak_hit_rate ? getStreakBadge(streak_hit_rate) : null;
  const contextStyle = getContextSignalStyle(streak_context_signal);
  const riskStyle = getRiskStyle(risk_level);

  // Build matchup string from available fields
  const matchup = opponent_name
    ? (is_home ? `vs ${opponent_name}` : `@ ${opponent_name}`)
    : (home_team && away_team ? `${away_team} @ ${home_team}` : null);

  // Format odds display
  const formatOdds = (odds) => {
    if (!odds) return null;
    return odds > 0 ? `+${odds}` : odds;
  };

  return (
    <div
      className={`glass-card p-4 transition-all hover:border-accent-blue/50 ${
        featured ? 'border-2 border-yellow-500/50 ring-1 ring-yellow-500/20' : ''
      }`}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Sport Badge */}
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${sportBg}`}>
            {sport?.toUpperCase()}
          </span>
          {/* Featured Badge */}
          {featured && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gradient-to-r from-yellow-500 to-amber-500 text-black">
              &#9733; FEATURED
            </span>
          )}
        </div>
        {/* Confidence Ring */}
        <ConfidenceRing value={composite_score} />
      </div>

      {/* Player Info */}
      <div className="mb-3">
        <h3 className="text-lg font-bold text-text-primary truncate">{player_name}</h3>
        <p className="text-sm text-text-secondary truncate">
          {team} {matchup ? `\u2022 ${matchup}` : ''}
        </p>
      </div>

      {/* Pick Direction */}
      <div className="mb-4 p-3 rounded-lg bg-bg-card">
        <div className="flex items-center justify-between">
          <div>
            <span className={`text-xl font-black ${pick_direction === 'over' ? 'text-green-400' : 'text-red-400'}`}>
              {pick_direction?.toUpperCase()}
            </span>
            <span className="text-xl font-bold text-text-primary ml-2">
              {line} {statLabel}
            </span>
          </div>
          {/* Odds */}
          {sportsbook_odds && (
            <div className="text-right">
              <span className="text-sm font-medium text-text-secondary">
                {formatOdds(sportsbook_odds)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {/* Streak Rate */}
        <div className="bg-bg-card p-2 rounded-lg text-center">
          <div className="text-xs text-text-secondary mb-1">Streak</div>
          <div className="text-sm font-bold text-text-primary">
            {streak_hit_rate ? `${Math.round(streak_hit_rate * 100)}%` : '-'}
          </div>
        </div>
        {/* Vegas Edge */}
        <div className="bg-bg-card p-2 rounded-lg text-center">
          <div className="text-xs text-text-secondary mb-1">Edge</div>
          <div className={`text-sm font-bold ${vegas_edge > 0 ? 'text-green-400' : vegas_edge < 0 ? 'text-red-400' : 'text-text-primary'}`}>
            {vegas_edge ? `${vegas_edge > 0 ? '+' : ''}${vegas_edge.toFixed(1)}` : '-'}
          </div>
        </div>
        {/* H2H or Confidence - Show only meaningful data */}
        <div className="bg-bg-card p-2 rounded-lg text-center">
          <div className="text-xs text-text-secondary mb-1">
            {vs_opponent_games > 0 ? 'H2H' : 'Score'}
          </div>
          <div className="text-sm font-bold text-text-primary">
            {vs_opponent_games > 0
              ? `${vs_opponent_hits || 0}/${vs_opponent_games}`
              : composite_score ? Math.round(composite_score) : '-'
            }
          </div>
        </div>
      </div>

      {/* Streak & H2H Row */}
      <div className="flex flex-wrap gap-2 mb-3">
        {/* Streak Badge */}
        {streakBadge && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${streakBadge.className}`}>
            {streakBadge.label}
          </span>
        )}
        {/* Streak Hit Rate */}
        {streak_hit_rate !== null && streak_hit_rate !== undefined && (
          <span className="text-xs text-text-secondary">
            L{current_streak || 10}: <span className="font-medium text-text-primary">{Math.round(streak_hit_rate * 100)}%</span>
          </span>
        )}
        {/* H2H Record */}
        {vs_opponent_games > 0 && (
          <span className="text-xs text-text-secondary">
            H2H: <span className="font-medium text-text-primary">{vs_opponent_hits || 0}/{vs_opponent_games}</span>
            {vs_opponent_rate && <span className="text-text-secondary"> ({Math.round(vs_opponent_rate * 100)}%)</span>}
          </span>
        )}
      </div>

      {/* Context Row */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
        {/* Home/Away Rate */}
        {is_home !== null && is_home !== undefined && (
          <span className="text-text-secondary">
            {is_home ? 'Home' : 'Away'}:{' '}
            <span className="font-medium text-text-primary">
              {Math.round((is_home ? player_home_rate : player_away_rate) * 100 || 0)}%
            </span>
          </span>
        )}
        {/* Context Signal */}
        {contextStyle && (
          <span className={`flex items-center gap-1 ${contextStyle.className}`}>
            <span>{contextStyle.icon}</span>
            {streak_context_signal}
          </span>
        )}
        {/* Opponent Strength */}
        {opponent_strength && (
          <span className="text-text-secondary">
            vs {opponent_strength}
          </span>
        )}
      </div>

      {/* Risk Level */}
      {riskStyle && (
        <div className="mb-3">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${riskStyle.className}`}>
            {risk_level?.toUpperCase()} RISK
          </span>
        </div>
      )}

      {/* Reasoning */}
      {reasoning && (
        <div className="pt-3 border-t border-border">
          <p className="text-xs text-text-secondary leading-relaxed line-clamp-3">
            {reasoning}
          </p>
        </div>
      )}
    </div>
  );
}
